/**
 * Descriptive alt text for listing photos (SEO + accessibility).
 *
 * Formula (per the Aug 2026 SEO spec):
 *   [title] – [colour], [størrelse X] – [what the photo shows]
 *
 * - Base is `listing.attributes.title`, NOT the brand field: sellers whose
 *   brand isn't in the dropdown pick "annet" and write the brand in the
 *   title, so the title is the reliable carrier of brand + garment type.
 * - Colour / size come from publicData (`farge`, `storrelse-klar` /
 *   `storrelse-sko`) resolved to their configured labels. Every part is
 *   optional and simply omitted when missing — no "undefined", no dangling
 *   commas.
 * - What the photo shows comes from `publicData.imageSlots` (front / back /
 *   label / care, keyed by image id), written by the photos step. Extra
 *   photos without a slot — and older listings from before imageSlots
 *   existed — fall back to "bilde n av N".
 */

const SLOT_MESSAGE_IDS = {
  front: 'ListingImageAlt.slotFront',
  back: 'ListingImageAlt.slotBack',
  label: 'ListingImageAlt.slotLabel',
  care: 'ListingImageAlt.slotCare',
};

const SIZE_FIELD_KEYS = ['storrelse-klar', 'storrelse-sko'];
const COLOUR_FIELD_KEY = 'farge';

const enumLabel = (listingFields, key, value) => {
  if (value == null || value === '') {
    return null;
  }
  const field = listingFields?.find(f => f.key === key);
  const match = (field?.enumOptions || []).find(o => o.option === value);
  return match?.label || String(value);
};

const colourText = (listingFields, publicData) => {
  const values = publicData?.[COLOUR_FIELD_KEY];
  const list = Array.isArray(values) ? values : values ? [values] : [];
  const labels = list.map(v => enumLabel(listingFields, COLOUR_FIELD_KEY, v)).filter(Boolean);
  return labels.length > 0 ? labels.join('/') : null;
};

const sizeText = (listingFields, publicData, intl) => {
  const key = SIZE_FIELD_KEYS.find(k => publicData?.[k] != null && publicData[k] !== '');
  if (!key) {
    return null;
  }
  const label = enumLabel(listingFields, key, publicData[key]);
  return label ? intl.formatMessage({ id: 'ListingImageAlt.size' }, { size: label }) : null;
};

const slotText = (publicData, image, index, total, intl) => {
  const imageId = image?.id?.uuid || image?.id;
  const slot = imageId ? publicData?.imageSlots?.[imageId] : null;
  const messageId = slot ? SLOT_MESSAGE_IDS[slot] : null;
  return messageId
    ? intl.formatMessage({ id: messageId })
    : intl.formatMessage({ id: 'ListingImageAlt.photoOfTotal' }, { index, total });
};

/**
 * Build the alt text for one listing image.
 *
 * @param {Object} params
 * @param {Object} params.listing - listing entity (needs attributes.title / publicData)
 * @param {Object} params.image - image entity (needs id)
 * @param {number} params.index - 1-based position of the image
 * @param {number} params.total - number of images
 * @param {Array} params.listingFields - config.listing.listingFields (for enum labels)
 * @param {Object} params.intl - react-intl
 * @returns {string|null} alt text, or null when there's no title to build from
 */
export const getListingImageAltText = ({ listing, image, index, total, listingFields, intl }) => {
  const title = (listing?.attributes?.title || '').trim();
  if (!title) {
    return null;
  }
  const publicData = listing?.attributes?.publicData || {};

  const details = [colourText(listingFields, publicData), sizeText(listingFields, publicData, intl)]
    .filter(Boolean)
    .join(', ');

  return [title, details, slotText(publicData, image, index, total, intl)]
    .filter(Boolean)
    .join(' – ');
};
